import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  IconButton,
  Typography,
  Collapse,
  Box,
  Chip,
} from "@material-ui/core";
import { ExpandMore, ExpandLess, OpenInNew } from "@material-ui/icons";
import { getBudgetStatus, AnimatedBudgetBadge } from "../../../BudgetBadge/BudgetBadge";

// Color palette for categories - consistent colors based on category name
const categoryColorPalette = [
  { name: "blue", borderLeft: "border-l-4 border-l-blue-500", borderTop: "border-t-2 border-t-blue-500", bg: "bg-blue-50", accent: "bg-blue-100", hex: "#3b82f6" },
  { name: "purple", borderLeft: "border-l-4 border-l-purple-500", borderTop: "border-t-2 border-t-purple-500", bg: "bg-purple-50", accent: "bg-purple-100", hex: "#a855f7" },
  { name: "indigo", borderLeft: "border-l-4 border-l-indigo-500", borderTop: "border-t-2 border-t-indigo-500", bg: "bg-indigo-50", accent: "bg-indigo-100", hex: "#6366f1" },
  { name: "teal", borderLeft: "border-l-4 border-l-teal-500", borderTop: "border-t-2 border-t-teal-500", bg: "bg-teal-50", accent: "bg-teal-100", hex: "#14b8a6" },
  { name: "amber", borderLeft: "border-l-4 border-l-amber-500", borderTop: "border-t-2 border-t-amber-500", bg: "bg-amber-50", accent: "bg-amber-100", hex: "#f59e0b" },
  { name: "rose", borderLeft: "border-l-4 border-l-rose-500", borderTop: "border-t-2 border-t-rose-500", bg: "bg-rose-50", accent: "bg-rose-100", hex: "#f43f5e" },
  { name: "emerald", borderLeft: "border-l-4 border-l-emerald-500", borderTop: "border-t-2 border-t-emerald-500", bg: "bg-emerald-50", accent: "bg-emerald-100", hex: "#10b981" },
  { name: "cyan", borderLeft: "border-l-4 border-l-cyan-500", borderTop: "border-t-2 border-t-cyan-500", bg: "bg-cyan-50", accent: "bg-cyan-100", hex: "#06b6d4" },
  { name: "pink", borderLeft: "border-l-4 border-l-pink-500", borderTop: "border-t-2 border-t-pink-500", bg: "bg-pink-50", accent: "bg-pink-100", hex: "#ec4899" },
  { name: "orange", borderLeft: "border-l-4 border-l-orange-500", borderTop: "border-t-2 border-t-orange-500", bg: "bg-orange-50", accent: "bg-orange-100", hex: "#f97316" },
  { name: "violet", borderLeft: "border-l-4 border-l-violet-500", borderTop: "border-t-2 border-t-violet-500", bg: "bg-violet-50", accent: "bg-violet-100", hex: "#8b5cf6" },
  { name: "sky", borderLeft: "border-l-4 border-l-sky-500", borderTop: "border-t-2 border-t-sky-500", bg: "bg-sky-50", accent: "bg-sky-100", hex: "#0ea5e9" },
];

// Generate consistent color for a category based on its name
const getCategoryColor = (categoryName) => {
  if (!categoryName) return categoryColorPalette[0];
  
  // Simple hash function to convert category name to a consistent index
  let hash = 0;
  for (let i = 0; i < categoryName.length; i++) {
    const char = categoryName.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  const index = Math.abs(hash) % categoryColorPalette.length;
  return categoryColorPalette[index];
};

const GridExpenseLine = (props) => {
  const [showExtra, setShowExtra] = useState(false);
  const hasExpenses = props.exp.exp_list && props.exp.exp_list.length > 0;
  // Determine if this is income/saving (green) or expense (red)
  const isIncome = props.exp.type === "income" || props.exp.type === "saving";

  // Map expense type to budget route
  const getBudgetRoute = (expenseType) => {
    const routeMap = {
      income: "/budget/income",
      saving: "/budget/saving",
      const_expense: "/budget/fixedexpenses",
      expense: "/budget/expenses",
    };
    return routeMap[expenseType] || "/budget/expenses";
  };

  // Get consistent color for this category based on its name
  const colors = getCategoryColor(props.exp.title);

  return (
    <Card 
      className={`mb-4 shadow-sm hover:shadow-md transition-shadow duration-200 ${colors.borderLeft} ${showExtra ? colors.bg : ""} ${hasExpenses ? 'cursor-pointer' : ''}`}
      onClick={hasExpenses ? () => setShowExtra(!showExtra) : undefined}
    >
      <CardContent className="p-4">
        {/* Main Category Row */}
        <div className="flex items-start gap-4">
          {/* Expand/Collapse Button */}
          {hasExpenses && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setShowExtra(!showExtra);
              }}
              className="flex-shrink-0 mt-1"
              aria-label={showExtra ? "Collapse expenses" : "Expand expenses"}
            >
              {showExtra ? (
                <ExpandLess className="text-slate-600" />
              ) : (
                <ExpandMore className="text-slate-600" />
              )}
            </IconButton>
          )}
          {!hasExpenses && <div className="w-10" />}

          {/* Category Content - Stacked on mobile */}
          <div className="flex-1 min-w-0">
            {/* First Line: Category Name + Total Amount (Mobile) */}
            <div className="flex items-center justify-between gap-2 mb-2 md:mb-0">
              {/* Category Name with Color Indicator */}
              <div className="flex-1 min-w-0 flex items-center gap-2">
                {/* Color indicator dot */}
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: colors.hex }}
                  title={`Category color: ${colors.name}`}
                />
                <Typography
                  variant="h6"
                  className="font-semibold text-slate-800"
                  style={{ fontSize: "1rem" }}
                >
                  {props.exp.title || "Uncategorized"}
                </Typography>
              </div>

              {/* Total Amount - Mobile: on same line, Desktop: separate */}
              <div className="flex-shrink-0 md:hidden">
                <Chip
                  label={`${props.exp.total < 0 ? '-' : ''}${props.symbol}${Math.abs(props.exp.total).toLocaleString()}`}
                  className={`font-bold ${
                    isIncome
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                  size="medium"
                />
              </div>
            </div>

            {/* Second Line: Budget Status (Mobile only) - Right aligned under total */}
            <div className="md:hidden flex justify-end mt-1">
              {props.categoriesWithBudget && props.exp.title && props.categoriesWithBudget[props.exp.title] && (() => {
                const budgetInfo = props.categoriesWithBudget[props.exp.title];
                const status = getBudgetStatus(budgetInfo.budget, budgetInfo.spent);
                
                return (
                  <AnimatedBudgetBadge
                    status={status}
                    size="sm"
                    title={`Budget: ${props.symbol}${budgetInfo.budget.toLocaleString()}, Spent: ${props.symbol}${budgetInfo.spent.toLocaleString()}, Remaining: ${props.symbol}${budgetInfo.remaining.toLocaleString()}`}
                  />
                );
              })()}
            </div>

            {/* Desktop: Total Amount and Budget Status on same line */}
            <div className="hidden md:flex md:items-center md:gap-2 md:justify-end">
              {props.categoriesWithBudget && props.exp.title && props.categoriesWithBudget[props.exp.title] && (() => {
                const budgetInfo = props.categoriesWithBudget[props.exp.title];
                const status = getBudgetStatus(budgetInfo.budget, budgetInfo.spent);
                
                return (
                  <AnimatedBudgetBadge
                    status={status}
                    title={`Budget: ${props.symbol}${budgetInfo.budget.toLocaleString()}, Spent: ${props.symbol}${budgetInfo.spent.toLocaleString()}, Remaining: ${props.symbol}${budgetInfo.remaining.toLocaleString()}`}
                  />
                );
              })()}
              <Chip
                label={`${props.exp.total < 0 ? '-' : ''}${props.symbol}${Math.abs(props.exp.total).toLocaleString()}`}
                className={`font-bold ${
                  isIncome
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
                size="medium"
              />
            </div>
          </div>
        </div>

        {/* Expanded Expense List */}
        <Collapse in={showExtra} timeout="auto" unmountOnExit>
          <Box className={`mt-4 pt-4 ${colors.borderTop}`}>
            <div className="space-y-3">
              {props.exp.exp_list &&
                props.exp.exp_list.map((exp, key) => (
                  <div
                    key={key}
                    className={`flex items-center gap-3 p-3 ${colors.accent} rounded-lg hover:opacity-80 transition-all border border-white/50`}
                  >
                    {/* Clickable Badge - Navigate to Budget */}
                    <Link
                      to={`${getBudgetRoute(exp.type)}${exp.id ? `?expenseId=${exp.id}` : ''}`}
                      className="flex-shrink-0 flex items-center justify-center w-8 h-8 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-md text-indigo-700 transition-all cursor-pointer shadow-sm hover:shadow-md group"
                      title={`View in ${exp.type === "income" ? "Income" : exp.type === "saving" ? "Savings" : exp.type === "const_expense" ? "Fixed Expenses" : "Expenses"} tab`}
                    >
                      <OpenInNew className="h-4 w-4 opacity-70 group-hover:opacity-100 transition-opacity" />
                    </Link>

                    {/* Expense Title */}
                    <div className="flex-1 min-w-0">
                      <Typography
                        variant="body2"
                        className="font-medium text-slate-700 truncate"
                      >
                        {exp.title || "Untitled"}
                      </Typography>
                    </div>

                    {/* Expense Amount */}
                    <div className="flex-shrink-0">
                      <Chip
                        label={
                          exp.type === "income"
                            ? `${props.symbol}${Number(exp.sum).toLocaleString()}`
                            : `-${props.symbol}${Number(exp.sum).toLocaleString()}`
                        }
                        size="small"
                        className={`font-medium ${
                          exp.type === "income"
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : "bg-red-50 text-red-700 border border-red-200"
                        }`}
                        variant="outlined"
                      />
                    </div>
                  </div>
                ))}
            </div>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default GridExpenseLine;
